/* Dashboard Builder — Analytics filter bar.
 *
 * Shown above a CUSTOM Analytics board so composed tiles narrow together by
 * broker / client / site / template / date. Filters start cleared on every
 * visit (not persisted). Mirrors the default Analytics page's searchable,
 * alphabetical dropdowns. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Input, Label, Select } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Filter, RefreshCw } from 'lucide-react';
import { DashFilters } from './types';

interface Lookup { id: string; name: string; trading_name?: string; broker_id?: string; client_id?: string }

const SearchableSelect: React.FC<{
  label: string; allLabel: string; value: string;
  options: Lookup[]; onChange: (v: string) => void; testid: string;
}> = ({ label, allLabel, value, options, onChange, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  const selected = options.find((o) => o.id === value);
  const selectedLabel = selected ? (selected.trading_name || selected.name) : allLabel;
  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter((o) => (o.trading_name || o.name || '').toLowerCase().includes(ql)) : options;
  return (
    <div className="space-y-1.5" ref={ref}>
      <Label>{label}</Label>
      <div className="relative">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid}
          className="w-48 h-9 px-3 rounded-md border border-border/60 bg-white text-sm text-left truncate flex items-center justify-between gap-2 hover:border-raptor-blue/60">
          <span className={`truncate ${value ? 'text-raptor-navy' : 'text-raptor-navy/50'}`}>{selectedLabel}</span>
          <Filter size={13} className="text-raptor-navy/40 shrink-0" />
        </button>
        {open && (
          <div className="absolute z-[1000] mt-1 w-48 bg-white border border-border/60 rounded-md shadow-lg" data-testid={`${testid}-panel`}>
            <div className="p-2 border-b border-border/50">
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8" />
            </div>
            <div className="max-h-64 overflow-auto py-1">
              <button type="button" onClick={() => { onChange(''); setOpen(false); setQ(''); }}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-raptor-neutral/40 ${!value ? 'font-semibold text-raptor-navy' : 'text-raptor-navy/70'}`}>{allLabel}</button>
              {filtered.map((o) => (
                <button key={o.id} type="button" onClick={() => { onChange(o.id); setOpen(false); setQ(''); }}
                  className={`w-full text-left px-3 py-1.5 text-sm truncate hover:bg-raptor-neutral/40 ${value === o.id ? 'font-semibold text-raptor-navy bg-raptor-blue/10' : 'text-raptor-navy/80'}`}>
                  {o.trading_name || o.name}
                </button>
              ))}
              {filtered.length === 0 && <div className="px-3 py-2 text-xs text-raptor-navy/40">No matches</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const iso = (d: Date) => d.toISOString().slice(0, 10);
function presetRange(key: string, cur: { from: string; to: string }): { from: string; to: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === 'all') return { from: '', to: '' };
  if (key === 'custom') return { from: cur.from, to: cur.to };
  if (key === 'yesterday') { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: iso(y), to: iso(y) }; }
  if (key === 'last7') { const s = new Date(today); s.setDate(s.getDate() - 6); return { from: iso(s), to: iso(today) }; }
  if (key === 'last30') { const s = new Date(today); s.setDate(s.getDate() - 29); return { from: iso(s), to: iso(today) }; }
  if (key === 'thismonth') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(today) };
  return { from: cur.from, to: cur.to };
}
function datePreset(from: string, to: string): string {
  if (!from && !to) return 'all';
  for (const k of ['yesterday', 'last7', 'last30', 'thismonth']) {
    const r = presetRange(k, { from: '', to: '' });
    if (r.from === from && r.to === to) return k;
  }
  return 'custom';
}

export const AnalyticsFilterBar: React.FC<{ filters: DashFilters; onChange: (f: DashFilters) => void }>
  = ({ filters, onChange }) => {
  const [clients, setClients] = useState<Lookup[]>([]);
  const [sites, setSites] = useState<Lookup[]>([]);
  const [brokers, setBrokers] = useState<Lookup[]>([]);
  const [templates, setTemplates] = useState<Lookup[]>([]);

  useEffect(() => {
    void (async () => {
      const sortBy = (arr: Lookup[]) => [...(arr || [])].sort((a, b) =>
        (a.trading_name || a.name || '').localeCompare(b.trading_name || b.name || ''));
      try {
        const [c, s, b, t] = await Promise.all([
          api.get<Lookup[]>('/clients?limit=10000'),
          api.get<Lookup[]>('/sites?limit=10000&fields=picker'),
          api.get<Lookup[]>('/brokers'),
          api.get<Lookup[]>('/templates'),
        ]);
        setClients(sortBy(c.data || []));
        setSites(sortBy(s.data || []));
        setBrokers(sortBy((b.data || []).map((x: any) => ({ id: x.id, name: x.brokerage_name || x.name }))));
        setTemplates(sortBy((t.data || []).map((x: any) => ({ id: x.id, name: x.name }))));
      } catch { /* ignore — filters simply stay empty */ }
    })();
  }, []);

  const f = filters;
  const set = (patch: Partial<DashFilters>) => onChange({ ...f, ...patch });
  const hasAny = !!(f.broker_id || f.client_id || f.site_id || f.template_id || f.from || f.to);

  // Scope Client + Site options to the chosen broker/client.
  const clientOptions = useMemo<Lookup[]>(
    () => (f.broker_id ? clients.filter((c) => c.broker_id === f.broker_id) : clients),
    [clients, f.broker_id],
  );
  const siteOptions = useMemo<Lookup[]>(
    () => sites.filter((s) =>
      (!f.broker_id || s.broker_id === f.broker_id)
      && (!f.client_id || s.client_id === f.client_id)),
    [sites, f.broker_id, f.client_id],
  );

  return (
    <div className="flex flex-wrap gap-3 items-end p-3 rounded-lg border border-border/60 bg-white" data-testid="analytics-custom-filterbar">
      <div className="space-y-1.5"><Label>Date range</Label>
        <Select value={datePreset(f.from || '', f.to || '')} onChange={(e) => set(presetRange(e.target.value, { from: f.from || '', to: f.to || '' }))} className="w-36" data-testid="custom-filter-dates">
          <option value="all">All dates</option>
          <option value="yesterday">Yesterday</option>
          <option value="last7">Last 7 days</option>
          <option value="last30">Last 30 days</option>
          <option value="thismonth">This month</option>
          <option value="custom">Custom…</option>
        </Select>
      </div>
      <div className="space-y-1.5"><Label>From</Label>
        <Input type="date" value={f.from || ''} onChange={(e) => set({ from: e.target.value })} data-testid="custom-filter-from" />
      </div>
      <div className="space-y-1.5"><Label>To</Label>
        <Input type="date" value={f.to || ''} onChange={(e) => set({ to: e.target.value })} data-testid="custom-filter-to" />
      </div>
      <SearchableSelect label="Broker" allLabel="All brokers" value={f.broker_id || ''} options={brokers} onChange={(v) => set({ broker_id: v, client_id: '', site_id: '' })} testid="custom-filter-broker" />
      <SearchableSelect label="Client" allLabel="All clients" value={f.client_id || ''} options={clientOptions} onChange={(v) => set({ client_id: v, site_id: '' })} testid="custom-filter-client" />
      <SearchableSelect label="Site" allLabel="All sites" value={f.site_id || ''} options={siteOptions} onChange={(v) => set({ site_id: v })} testid="custom-filter-site" />
      <SearchableSelect label="Survey template" allLabel="All templates" value={f.template_id || ''} options={templates} onChange={(v) => set({ template_id: v })} testid="custom-filter-template" />
      {hasAny && (
        <Button variant="outline" size="sm" onClick={() => onChange({})} data-testid="custom-filter-clear">
          <RefreshCw size={14} /> Clear
        </Button>
      )}
    </div>
  );
};
